import { useState, useEffect, useCallback, useRef } from "react";
import * as db from "../lib/db";
import * as ai from "../lib/ai";
import { DEFAULT_EXERCISES } from "../data/exercises";
import { todayKey } from "../utils/helpers";
import { getRunPlan } from "../utils/run-plan";
import { trackAppOpen } from "../lib/analytics";
import * as moodStore from "../lib/mood-store";

export function useAppData(userId, { setLoading }) {
  const [profile, setProfile] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [exercises, setExercises] = useState(DEFAULT_EXERCISES);
  const [mealLog, setMealLog] = useState({});
  const [customMeals, setCustomMeals] = useState([]);
  const [personalRecords, setPersonalRecords] = useState({});
  const [userTemplates, setUserTemplates] = useState([]);
  const [weightLog, setWeightLog] = useState([]);
  const [macroGoals, setMacroGoals] = useState(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [targetWeight, setTargetWeight] = useState(null);
  const [weightReminderTime, setWeightReminderTime] = useState(null);
  const [pendingMacros, setPendingMacros] = useState(null);
  const [todayAiWorkout, setTodayAiWorkout] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [runPlan, setRunPlan] = useState(null);
  const [runPlanLoading, setRunPlanLoading] = useState(false);
  const [moodCheckins, setMoodCheckins] = useState([]);
  // Whether the check-in set is trustworthy yet. Home refuses to decide whether
  // to run the daily ritual until this is true — see localCheckins() above.
  const [moodLoaded, setMoodLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const loadTimerRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ── Check-ins, immediately ──
  // The device holds the working copy, so today's answer is readable without a
  // round trip. Painting it now means Home never renders a "have you checked
  // in?" state to somebody who checked in an hour ago.
  useEffect(() => {
    if (!userId) return;
    const local = moodStore.localCheckins(userId);
    if (local.length) setMoodCheckins(local);
  }, [userId]);

  // ── Instant resume ──
  // Hydrate from the last successful load so the app paints real content
  // immediately; the network load below then replaces it with fresh data.
  // (Sign-out clears localStorage, so the cache never crosses accounts.)
  const CACHE_V = 1;
  useEffect(() => {
    if (!userId) return;
    try {
      const raw = localStorage.getItem(`appcache_${userId}`);
      if (!raw) return;
      const c = JSON.parse(raw);
      if (c.v !== CACHE_V || Date.now() - (c.ts || 0) > 7 * 86400000) return;
      if (c.prof) {
        setProfile(c.prof);
        if (c.prof.macro_goals) setMacroGoals(c.prof.macro_goals);
        if (c.prof.target_weight) setTargetWeight(c.prof.target_weight);
      }
      setWorkouts(c.w || []);
      setRuns(c.r || []);
      if (Array.isArray(c.ue) && c.ue.length) {
        const customIds = new Set(c.ue.map((e) => e.id));
        setExercises([...DEFAULT_EXERCISES.filter((e) => !customIds.has(e.id)), ...c.ue]);
      }
      setMealLog(c.ml || {});
      setCustomMeals(c.cm || []);
      setPersonalRecords(c.pr || {});
      setUserTemplates(c.ut || []);
      setWeightLog(c.wl || []);
      if (c.ob) {
        setOnboardingCompleted(c.ob.onboardingCompleted === true);
        setUserProfile(c.ob);
      }
      // Cached AI workout is only valid if it was generated today
      if (c.tw?.created_at && new Date(c.tw.created_at).toDateString() === new Date().toDateString()) {
        setTodayAiWorkout(c.tw);
      }
      setLoading(false); // paint now; fresh data lands silently
    } catch { /* corrupted cache — normal load takes over */ }
  }, [userId, setLoading]);

  // ── Loading failsafe ──
  // getSession() resolves from localStorage even when the backend is
  // unreachable, so the app proceeds to load data — and a hung fetch (paused
  // project, black-holed network) never settles, so Promise.all never resolves
  // and setLoading(false) is never reached. That produced an endless spinner.
  // This guarantees the UI renders no matter what: worst case the user gets an
  // empty-but-usable app plus the offline banner, never a dead loading screen.
  useEffect(() => {
    if (!userId) return;
    const t = setTimeout(() => {
      setLoading(false);
      setLoadFailed(true);
    }, 12_000);
    loadTimerRef.current = t;
    return () => clearTimeout(t);
  }, [userId, setLoading]);

  // Load all data when user logs in
  useEffect(() => {
    if (!userId) return;

    // Keep the profile's stored timezone current so server-side "today"
    // calculations (coaching, streaks, crons) match the user's local day.
    db.syncTimezone(userId);
    trackAppOpen();

    const load = async () => {
      try {
        const [prof, w, r, ue, ml, cm, pr] = await Promise.all([
          db.getProfile(userId).catch(() => null),
          db.fetchWorkouts(userId).catch(() => []),
          db.fetchRuns(userId).catch(() => []),
          db.fetchUserExercises(userId).catch(() => []),
          db.fetchMealLog(userId).catch(() => ({})),
          db.fetchCustomMeals(userId).catch(() => []),
          db.fetchPersonalRecords(userId).catch(() => ({})),
        ]);

        setProfile(prof);
        setWorkouts(w);
        setRuns(r);
        const customIds = new Set(ue.map((e) => e.id));
        setExercises([
          ...DEFAULT_EXERCISES.filter((e) => !customIds.has(e.id)),
          ...ue,
        ]);
        setMealLog(ml);
        setCustomMeals(cm);
        setPersonalRecords(pr);
        if (prof?.macro_goals) setMacroGoals(prof.macro_goals);
        if (prof?.target_weight) setTargetWeight(prof.target_weight);
        if (prof?.weight_reminder_time) setWeightReminderTime(prof.weight_reminder_time);

        const [ut, wl, ob, tw] = await Promise.all([
          db.fetchUserTemplates(userId).catch(() => []),
          db.fetchWeightLog(userId).catch(() => []),
          db.fetchOnboarding(userId).catch(() => null),
          ai.fetchTodayWorkout(userId).catch(() => null),
        ]);
        moodStore.loadCheckins(userId).then((all) => { setMoodCheckins(all); setMoodLoaded(true); });
        setUserTemplates(ut);
        setWeightLog(wl);
        setOnboardingCompleted(ob?.onboardingCompleted === true);
        if (ob) setUserProfile(ob);
        if (tw) setTodayAiWorkout(tw);

        // Snapshot for instant resume on next launch
        try {
          localStorage.setItem(`appcache_${userId}`, JSON.stringify({
            v: 1, ts: Date.now(),
            prof, w: w.slice(0, 150), r: r.slice(0, 150), ue,
            ml, cm, pr, ut, wl: wl.slice(0, 100), ob, tw,
          }));
        } catch { /* storage full — instant resume just won't apply */ }

        getRunPlan(userId)
          .then((p) => { if (p) setRunPlan(p); })
          .catch((e) => { console.warn("Failed to load run plan:", e.message); });

        ai.getProactiveInsights(userId)
          .then((data) => { if (data?.insights?.length) setAiInsights(data.insights); })
          .catch((e) => { console.warn("Failed to load proactive insights:", e.message); });
      } catch (e) {
        console.error("Failed to load data:", e);
        setLoadFailed(true);
      }
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
      setLoading(false);
    };

    load();
  }, [userId, setLoading]);

  // Midnight refresh: re-fetch meal log when the date changes
  useEffect(() => {
    if (!userId) return;
    let currentDay = todayKey();

    const interval = setInterval(async () => {
      const newDay = todayKey();
      if (newDay !== currentDay) {
        currentDay = newDay;
        try {
          const ml = await db.fetchMealLog(userId);
          setMealLog(ml);
        } catch (e) {
          console.warn("Midnight refresh failed:", e.message);
        }
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [userId]);

  const handleRefresh = useCallback(async () => {
    if (!userId || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const [ml, tw, w, r, wl, pr] = await Promise.all([
        db.fetchMealLog(userId).catch(() => null),
        ai.fetchTodayWorkout(userId).catch(() => null),
        db.fetchWorkouts(userId).catch(() => null),
        db.fetchRuns(userId).catch(() => null),
        db.fetchWeightLog(userId).catch(() => null),
        db.fetchPersonalRecords(userId).catch(() => null),
      ]);
      if (ml) setMealLog(ml);
      if (tw) setTodayAiWorkout(tw);
      if (w) setWorkouts(w);
      if (r) setRuns(r);
      if (wl) setWeightLog(wl);
      if (pr) setPersonalRecords(pr);
      moodStore.loadCheckins(userId).then((all) => { setMoodCheckins(all); setMoodLoaded(true); });
      ai.getProactiveInsights(userId)
        .then((result) => { if (result?.insights?.length) setAiInsights(result.insights); })
        .catch((e) => console.warn("Failed to load proactive insights:", e));
    } catch (e) {
      console.warn("Refresh failed:", e.message);
    }
    setIsRefreshing(false);
  }, [userId, isRefreshing]);

  const reset = useCallback(() => {
    setProfile(null);
    setWorkouts([]);
    setRuns([]);
    setExercises(DEFAULT_EXERCISES);
    setMealLog({});
    setCustomMeals([]);
    setPersonalRecords({});
    setUserTemplates([]);
    setWeightLog([]);
    setMacroGoals(null);
    setOnboardingCompleted(null);
    setUserProfile(null);
    setTargetWeight(null);
    setWeightReminderTime(null);
    setPendingMacros(null);
    setTodayAiWorkout(null);
    setAiInsights([]);
    setRunPlan(null);
    setRunPlanLoading(false);
    setMoodCheckins([]);
    setMoodLoaded(false);
  }, []);

  return {
    profile, setProfile,
    workouts, setWorkouts,
    runs, setRuns,
    exercises, setExercises,
    mealLog, setMealLog,
    customMeals, setCustomMeals,
    personalRecords, setPersonalRecords,
    userTemplates, setUserTemplates,
    weightLog, setWeightLog,
    macroGoals, setMacroGoals,
    onboardingCompleted, setOnboardingCompleted,
    userProfile, setUserProfile,
    targetWeight, setTargetWeight,
    weightReminderTime, setWeightReminderTime,
    pendingMacros, setPendingMacros,
    todayAiWorkout, setTodayAiWorkout,
    aiInsights, setAiInsights,
    runPlan, setRunPlan,
    runPlanLoading, setRunPlanLoading,
    moodCheckins, setMoodCheckins,
    moodLoaded,
    loadFailed,
    isRefreshing,
    handleRefresh,
    reset,
  };
}
